import { Injectable } from '@angular/core';

const STORAGE_KEY = 'budgets.pendingPromoCode';
/** How long a code from an ad link waits for the visitor to reach billing. */
const PENDING_PROMO_CODE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 30;
/** Stripe promotion codes: letters, digits, dashes and underscores, up to the API's 100 characters. */
const PROMO_CODE_PATTERN = /^[A-Za-z0-9_-]{1,100}$/;

interface StoredPromoCode {
  code: string;
  savedAt: number;
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Saves a promotion code from an ad or marketing link (`?promo=CODE` on any
 * app URL) so it survives sign-up and is applied when the visitor reaches
 * the billing step. Runs before the app boots, while the landing URL still
 * has its query string.
 */
export function capturePromoCodeFromUrl(
  url: string = window.location.href,
  storage: Storage | null = safeStorage(),
  now: number = Date.now(),
): void {
  if (storage == null) return;
  let code: string | null;
  try {
    code = new URL(url).searchParams.get('promo')?.trim() ?? null;
  } catch {
    return;
  }
  if (code == null || !PROMO_CODE_PATTERN.test(code)) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ code, savedAt: now } satisfies StoredPromoCode));
  } catch {
    // Storage full or blocked: the code can still be typed in by hand.
  }
}

/**
 * Saves the landing URL's code, then takes `promo` out of the address bar
 * (keeping the rest of the query and hash) so a reload can't bring back a
 * code the member removed on the billing step.
 */
export function consumePromoCodeFromLocation(
  location: Pick<Location, 'href'> = window.location,
  history: Pick<History, 'state' | 'replaceState'> = window.history,
): void {
  capturePromoCodeFromUrl(location.href);
  let url: URL;
  try {
    url = new URL(location.href);
  } catch {
    return;
  }
  if (!url.searchParams.has('promo')) return;
  url.searchParams.delete('promo');
  history.replaceState(history.state, '', url.toString());
}

@Injectable({ providedIn: 'root' })
export class PendingPromoCodeService {
  private readonly storage = safeStorage();

  /** The saved code, if one arrived by link within the last 30 days. */
  pendingCode(now: number = Date.now()): string | null {
    const raw = this.read();
    if (raw == null) return null;
    if (now - raw.savedAt > PENDING_PROMO_CODE_MAX_AGE_MS) {
      this.clear();
      return null;
    }
    return raw.code;
  }

  clear(): void {
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      // Nothing to clear.
    }
  }

  private read(): StoredPromoCode | null {
    try {
      const value: unknown = JSON.parse(this.storage?.getItem(STORAGE_KEY) ?? 'null');
      if (
        typeof value === 'object' &&
        value != null &&
        typeof (value as StoredPromoCode).code === 'string' &&
        typeof (value as StoredPromoCode).savedAt === 'number'
      )
        return value as StoredPromoCode;
    } catch {
      // Unreadable entry: treat as none.
    }
    return null;
  }
}
